<template>
    <transition
        enter-active-class="ease-out duration-300"
        enter-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enter-to-class="opacity-100 translate-y-0 sm:scale-100"
        leave-active-class="ease-in duration-200"
        leave-class="opacity-100 translate-y-0 sm:scale-100"
        leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
        <div
            v-show="show"
            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm md:max-w-md sm:w-full sm:p-6" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
            <template v-if="visible">
                <form method="POST" @submit.prevent="section === 'payment-authorization' ? reschedule() : previewOrAccept()">
                    <div class="mb-4">
                        <div :class="`mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-${$root.theme}-100`">
                            <svg :class="`h-8 w-8 text-${$root.theme}-600`" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div class="mt-4">
                            <h3 class="text-lg mb-2 leading-6 font-medium text-gray-900 text-center" id="modal-headline">
                                Make Additional Payment
                            </h3>
                            <template v-if="section !== 'payment-authorization'">

                                <p class="text-gray-500 mb-4 px-6 text-center">
                                    Use this option to make an additional payment today.
                                </p>
                                <div class="mb-4 mt-1 flex rounded-md shadow-sm">
                                    <span class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                                        $
                                    </span>
                                    <input v-model="form.payment.amount"
                                           type="text"
                                           name="amount"
                                           id="amount"
                                           :class="`bg-gray-50 focus:ring-${$root.theme}-400 focus:border-${$root.theme}-400 block w-full py-3 pl-4 pr-4 sm:text-sm border-gray-300 rounded-t rounded-r rounded-b`"
                                           placeholder="Payment Amount">
                                </div>

                                <select v-model="form.payment.type"
                                        :id="`${unique}_payment_method`"
                                        name="payment_method"
                                        :class="`bg-gray-50 focus:ring-${$root.theme}-400 focus:border-${$root.theme}-400 mt-1 block w-full pl-4 pr-10 py-3 text-base border-gray-300 focus:outline-none sm:text-sm rounded-md`">
                                    <option :value="null">Choose Method:</option>
                                    <option value="same" v-if="billingProfile && billingProfile.payment_type">
                                        {{ billingProfile.payment_type === 'check' ? 'Checking' : billingProfile.cc_type }} ••••
                                        {{ billingProfile.account_last_4 }}
                                    </option>
                                    <option v-if="settings.enable_ach" value="check">Pay with Personal Bank account</option>
                                    <option value="card">Pay with Credit/Debit Card</option>
                                </select>

                                <pay-with-bank v-if="form.payment.type === 'check'"
                                               :first_name.sync="form.payment.first_name"
                                               :last_name.sync="form.payment.last_name"
                                               :routing_number.sync="form.payment.routing_number"
                                               :account_number.sync="form.payment.account_number"
                                               :error="error" class="mt-4" />

                                <pay-with-card v-else-if="form.payment.type === 'card'"
                                               :first_name.sync="form.payment.first_name"
                                               :last_name.sync="form.payment.last_name"
                                               :address.sync="form.payment.address"
                                               :city.sync="form.payment.city"
                                               :state.sync="form.payment.state"
                                               :cc_number.sync="form.payment.cc_number"
                                               :card_exp.sync="form.payment.card_exp"
                                               :cvv.sync="form.payment.cvv"
                                               :zip.sync="form.payment.zip"
                                               :settings="settings"
                                               :cc-valid-status="ccValidStatus"
                                               :error="error" class="mt-4" />

                                <p class="text-gray-500 mt-4 px-6 text-sm text-center">
                                    Future payments will be recalculated based on your new balance.
                                </p>

                            </template>
                            <div v-else
                                v-html="paymentAuthorizationAgreementTemplate"
                                class="overflow-y-auto max-h-96 p-4 my-4 border-2 border-gray-200 rounded-lg text-sm"
                            />
                        </div>
                    </div>
                    <p v-if="error" class="mb-2 text-sm text-red-600" id="email-error">Card declined.</p>
                    <div class="sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                        <button type="submit"
                            :class="{
                                [`w-full inline-flex text-black items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-${$root.theme}-300 text-base font-medium hover:bg-${$root.theme}-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${$root.theme}-500 sm:col-start-2 sm:text-sm`]: true,
                                'cursor-not-allowed opacity-50': ! readyToSubmit
                            }"
                            :disabled="! readyToSubmit">
                            <template v-if="! loading">
                                {{ section !== 'payment-authorization' ? 'Continue' : 'Schedule Payments' }}
                            </template>
                            <template v-else>
                                <i :class="`fa fa-spinner fa-spin text-white`"></i>
                            </template>
                        </button>
                        <button @click="section !== 'payment-authorization' ? nevermind() : previewDeclined()" type="button" :class="`mt-3 w-full inline-flex items-center justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${$root.theme}-500 sm:mt-0 sm:col-start-1 sm:text-sm`">
                            {{ section !== 'payment-authorization' ? 'Never mind' : 'Go Back' }}
                        </button>
                    </div>
                </form>
            </template>
        </div>
    </transition>
</template>

<script>
    import Moment from 'moment';
    import Mixins from '../../mixins';
    import PayWithCard from '../PaymentMethods/PayWithCard';
    import PayWithBank from '../PaymentMethods/PayWithBank';
    import PaymentAuthorization from "../../components/PaymentAuthorization.vue";
    import OfferSummary from "../OfferSummary.vue";

    export default {
        mixins: [Mixins],

        components: {
            PaymentAuthorization,
            PayWithBank,
            PayWithCard
        },

        props: ['show', 'account', 'billing-profile', 'settings'],

        created() {
            this.unique = Math.floor(Math.random() * 100);
        },

        /**
         * Because this entire component is created/mounted by not visible for transitions
         * most life cycle events have to have their logic tied in during
         * changes to "show"
         *
         * @returns {void}
         */
        watch: {
            show: {
                immediate: true,
                handler: function (show, oldShow) {
                    if (show && ! oldShow) {
                        this.form.payment = {
                            type: ! this.settings.enable_ach && ! this.billingProfile ? 'card' : this.billingProfile ? 'same' : 'check',
                            billing_profile_id: null,
                            payment_date: Moment().format('YYYY-MM-DD'),
                            frequency: 'monthly',
                            email: '',
                            first_name: '',
                            last_name: '',
                            address: '',
                            city: '',
                            state: '',

                            routing_number: '',
                            account_number: '',

                            cc_number: '',
                            card_exp: '',
                            exp_month: '',
                            exp_year: '',
                            cvv: '',
                            zip: ''
                        };

                        if (type === 'same') {
                            this.form.payment.billing_profile_id = this.billingProfile.id;
                        }
                    }

                    if (! show && ! oldShow) {
                        // Mounted...
                    }
                }
            },

            'form.payment.type'(type, oldType) {
                if (type === 'same') {
                    this.form.payment.billing_profile_id = this.billingProfile.id;
                } else {
                    this.form.payment.billing_profile_id = null;
                }
            }
        },

        data() {
            return {
                section: 'schedule',
                unique: null,
                error: null,
                loading: false,
                form: {
                    payment: null,
                },
                paymentAuthorizationAgreementTemplate: null,
                paymentAuthorizationTransactions: null,
                forPreview: false,
            }
        },

        computed: {
            ccValidStatus() {
                if (/[^0-9-\s]+/.test(this.form.payment.cc_number)) return false;

                // The Luhn Algorithm. It's so pretty.
                var nCheck = 0,
                    nDigit = 0,
                    bEven = false;

                let value = this.form.payment.cc_number.replace(/\D/g, "");

                for (var n = value.length - 1; n >= 0; n--) {
                    var cDigit = value.charAt(n),
                        nDigit = parseInt(cDigit, 10);

                    if (bEven) {
                        if ((nDigit *= 2) > 9) nDigit -= 9;
                    }

                    nCheck += nDigit;
                    bEven = !bEven;
                }

                return (nCheck % 10) == 0;
            },

            /**
             * @returns {*}
             */
            payment() {
                return this.form.payment;
            },

            /**
             * Checks most of the props.
             *
             * @returns {void}
             */
            visible() {
                return this.show;
            },

            /**
             * @returns {String}
             */
            tomorrow() {
                return new Moment().add(1, 'days').format('YYYY-MM-DD')
            },

            /**
             * @returns {boolean}
             */
            readyToSubmit() {
                return !! this.form.payment?.amount;
            }
        },

        methods: {
            /**
             * @returns {String}
             */
            today(plusDays) {
                return new Moment().add(plusDays || 0, 'day').format('YYYY-MM-DD');
            },

            /**
             * @returns {String}
             */
            lastPaymentDate(plusDays) {
                if (this.lastPossibleRescheduleDate) {
                    return new Moment(this.lastPossibleRescheduleDate).add(plusDays, 'day').format('YYYY-MM-DD');
                }
            },

            previewDeclined() {
                this.section = 'reschedule';

                this.forPreview = false;

                this.$root.$refs.app.updatePageName('offers');
            },

            previewOrAccept() {
                this.forPreview = this.account.obtain_payment_authorization && this.$parent.settings.feature_payment_authorization_agreement;

                return this.reschedule();
            },

            /**
             * @return {void|null}
             */
            reschedule() {
                this.error = false;

                if (this.loading) return;

                this.loading = true;

                this.submitPayment({
                    submittedPaymentMethod: {
                        ...this.form.payment,
                    },
                    apiUrl: `${this.$parent.consumerPortalBaseUrl}/api/extra-payment${this.forPreview ? '/preview' : ''}`,
                    afterCallback: ({ data }) => {
                        if (this.forPreview) {
                            this.forPreview = false;

                            this.paymentAuthorizationAgreementTemplate = data.payment_authorization_agreement_template;
                            this.paymentAuthorizationTransactions = data.transactions;

                            this.section = 'payment-authorization';

                            this.gtagEvent('payment-authorization-requested');
                        } else {
                            if (this.account.obtain_payment_authorization && this.$parent.settings.feature_payment_authorization_agreement) {
                                this.gtagEvent('payment-authorization-accepted');
                            }

                            this.gtagEvent('offer-accepted:Extra-Payment');

                            setTimeout(() => {
                                window.location.reload();

                                this.$emit('close');
                            }, 1000);
                        }

                        this.loading = false;
                    },
                    onError: error => {
                        if (error.hasOwnProperty('body')) {
                            if (error.body.hasOwnProperty('errors') && error.body.errors.hasOwnProperty('zip')) {
                                this.error = error.body.errors.zip[0];
                            } else if (error.body.hasOwnProperty('errors')) {
                                this.error = error.body.errors;
                            } else if (error.body.hasOwnProperty('error')) {
                                this.error = error.body.error;
                            }

                            this.section = 'reschedule';
                        } else {
                            throw error;
                        }

                        this.loading = false;
                    },
                    withLoading: false
                })
            },

            /**
             * @return {void}
             */
            nevermind() {
                this.$emit('close');
            },
        }
    }
</script>
